import { ChangeDetectionStrategy, Component, inject, signal, computed, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    LucideAngularModule,
    FileText, Check, AlertCircle, Upload, RefreshCw, CheckCircle,
    AlertOctagon, Loader, X, Eye, Clock, Filter, FilterX, Calendar,
    Send, Plus, RotateCcw, ChevronDown, ChevronRight, Package
} from 'lucide-angular';
import {
    DemandImportService,
    DemandImportResponse,
    DemandActiveCode,
    DemandImportBranch,
    DemandImportProduct
} from '../../services/demand-import.service';
import { AuthService } from '../../services/auth.service';

type DemandStatus = 'IMPORTED' | 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'ERROR';
type GridColumn = 'fileName' | 'demandCode' | 'status' | 'createdAt';

@Component({
    selector: 'app-demand-import',
    templateUrl: './demand-import.html',
    styleUrls: ['./demand-import.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, LucideAngularModule],
})
export class DemandImportComponent {
    readonly #service = inject(DemandImportService);
    readonly #destroyRef = inject(DestroyRef);
    readonly authService = inject(AuthService);

    readonly icons = {
        FileText, Check, AlertCircle, Upload, RefreshCw, CheckCircle,
        AlertOctagon, Loader, X, Eye, Clock, Filter, FilterX, Calendar,
        Send, Plus, RotateCcw, ChevronDown, ChevronRight, Package
    };

    // --- GRID STATE ---
    readonly imports = signal<DemandImportResponse[]>([]);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);

    // --- GRID FILTERS ---
    readonly activeFilterColumn = signal<GridColumn | null>(null);
    readonly filterFileName = signal('');
    readonly filterDemandCode = signal('');
    readonly filterStatusValues = signal<Set<string>>(new Set());
    readonly filterDateStart = signal('');
    readonly filterDateEnd = signal('');

    readonly statusOptions: { label: string; value: DemandStatus }[] = [
        { label: 'Aguardando Agendamento', value: 'IMPORTED' },
        { label: 'Na Fila', value: 'PENDING' },
        { label: 'Processando', value: 'PROCESSING' },
        { label: 'Concluído', value: 'SUCCESS' },
        { label: 'Erro', value: 'ERROR' },
    ];

    readonly hasActiveFilters = computed(() =>
        this.filterFileName() !== '' ||
        this.filterDemandCode() !== '' ||
        this.filterStatusValues().size > 0 ||
        this.filterDateStart() !== '' ||
        this.filterDateEnd() !== ''
    );

    readonly filteredImports = computed(() => {
        let data = [...this.imports()];

        const fileNameTerm = this.filterFileName().toLowerCase();
        if (fileNameTerm) {
            data = data.filter(r => (r.fileName || '').toLowerCase().includes(fileNameTerm));
        }

        const demandCodeTerm = this.filterDemandCode().toLowerCase();
        if (demandCodeTerm) {
            data = data.filter(r => (r.demandCode || '').toLowerCase().includes(demandCodeTerm));
        }

        const statusSet = this.filterStatusValues();
        if (statusSet.size > 0) {
            data = data.filter(r => statusSet.has(r.status));
        }

        const dateStart = this.filterDateStart();
        const dateEnd = this.filterDateEnd();
        if (dateStart) {
            const start = new Date(dateStart);
            start.setHours(0, 0, 0, 0);
            data = data.filter(r => new Date(r.createdAt) >= start);
        }
        if (dateEnd) {
            const end = new Date(dateEnd);
            end.setHours(23, 59, 59, 999);
            data = data.filter(r => new Date(r.createdAt) <= end);
        }

        return data.sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
    });

    // --- SCHEDULE MODAL STATE ---
    readonly scheduleModalImport = signal<DemandImportResponse | null>(null);
    readonly activeCodes = signal<DemandActiveCode[]>([]);
    readonly activeCodesLoading = signal(false);
    readonly selectedDemandCode = signal<string | null>(null);
    readonly scheduling = signal(false);
    readonly scheduleError = signal<string | null>(null);

    // --- DETAILS MODAL STATE ---
    readonly selectedImport = signal<DemandImportResponse | null>(null);
    readonly branches = signal<DemandImportBranch[]>([]);
    readonly branchesLoading = signal(false);
    readonly branchesError = signal<string | null>(null);
    readonly retryingBranch = signal<string | null>(null);
    readonly expandedBranch = signal<string | null>(null);
    readonly branchProducts = signal<DemandImportProduct[]>([]);
    readonly branchProductsLoading = signal(false);

    // --- DETAILS FILTERS ---
    readonly detailBranchFilter = signal('');
    readonly detailStatusFilter = signal<Set<string>>(new Set());
    readonly activeDetailFilter = signal<'branchCode' | 'status' | null>(null);

    readonly detailBranchStatusOptions: { label: string; value: string }[] = [
        { label: 'Pendente', value: 'PENDING' },
        { label: 'Processando', value: 'PROCESSING' },
        { label: 'Sucesso', value: 'SUCCESS' },
        { label: 'Erro', value: 'ERROR' },
    ];

    readonly branchCounts = computed(() => {
        const list = this.branches();
        return {
            total: list.length,
            success: list.filter(b => b.status === 'SUCCESS').length,
            error: list.filter(b => b.status === 'ERROR').length,
            pending: list.filter(b => b.status === 'PENDING').length,
            processing: list.filter(b => b.status === 'PROCESSING').length,
        };
    });

    readonly filteredBranches = computed(() => {
        let list = [...this.branches()];
        const branchTerm = this.detailBranchFilter().toLowerCase();
        const statusSet = this.detailStatusFilter();

        if (branchTerm) {
            list = list.filter(b => b.branchCode.toLowerCase().includes(branchTerm));
        }
        if (statusSet.size > 0) {
            list = list.filter(b => statusSet.has(b.status));
        }
        return list;
    });

    readonly hasActiveDetailFilters = computed(() =>
        this.detailBranchFilter() !== '' || this.detailStatusFilter().size > 0
    );

    readonly successPercentage = computed(() => {
        const counts = this.branchCounts();
        if (counts.total === 0) return 0;
        return Math.round((counts.success / counts.total) * 100);
    });

    readonly isPollingActive = signal(false);

    // --- TOAST ---
    readonly toast = signal<{ type: 'success' | 'error'; message: string } | null>(null);

    // --- POLLING ---
    #pollingInterval: ReturnType<typeof setInterval> | null = null;

    constructor() {
        this.loadData();
        this.#destroyRef.onDestroy(() => this.#stopPolling());
    }

    // ========== DATA LOADING ==========

    loadData(): void {
        this.loading.set(true);
        this.error.set(null);

        this.#service.getAll().subscribe({
            next: (data) => {
                this.imports.set(data);
                this.loading.set(false);
            },
            error: (err) => {
                console.error('Erro ao carregar importações:', err);
                this.error.set('Falha ao carregar os dados. Tente novamente.');
                this.loading.set(false);
            }
        });
    }

    // ========== SCHEDULE MODAL ==========

    openScheduleModal(item: DemandImportResponse): void {
        this.scheduleModalImport.set(item);
        this.selectedDemandCode.set(null);
        this.scheduleError.set(null);
        this.activeCodesLoading.set(true);

        this.#service.getActiveCodes().subscribe({
            next: (codes) => {
                this.activeCodes.set(codes);
                this.activeCodesLoading.set(false);
            },
            error: (err) => {
                console.error('Erro ao carregar códigos ativos:', err);
                this.activeCodes.set([]);
                this.activeCodesLoading.set(false);
                this.scheduleError.set('Falha ao carregar códigos de demanda.');
            }
        });
    }

    closeScheduleModal(): void {
        this.scheduleModalImport.set(null);
        this.activeCodes.set([]);
        this.selectedDemandCode.set(null);
        this.scheduleError.set(null);
    }

    selectDemandCode(code: string): void {
        this.selectedDemandCode.set(this.selectedDemandCode() === code ? null : code);
    }

    confirmSchedule(): void {
        const importItem = this.scheduleModalImport();
        const code = this.selectedDemandCode();
        if (!importItem || !code) return;

        this.scheduling.set(true);
        this.scheduleError.set(null);

        this.#service.schedule(importItem.uuid, code).subscribe({
            next: () => {
                this.scheduling.set(false);
                this.closeScheduleModal();
                this.showToast('success', 'Importação agendada com sucesso!');
                this.loadData();

                // Abre os detalhes automaticamente após agendar
                const updatedItem: DemandImportResponse = {
                    ...importItem,
                    status: 'PENDING',
                    demandCode: code
                };
                this.openDetails(updatedItem);
            },
            error: (err) => {
                console.error('Erro ao agendar importação:', err);
                const customerMsg = err.error?.messageCustomer || 'Falha ao agendar a importação. Tente novamente.';
                this.scheduleError.set(customerMsg);
                this.scheduling.set(false);
            }
        });
    }

    // ========== DETAILS MODAL ==========

    openDetails(item: DemandImportResponse): void {
        this.selectedImport.set(item);
        this.branches.set([]);
        this.branchesLoading.set(true);
        this.branchesError.set(null);
        this.expandedBranch.set(null);
        this.branchProducts.set([]);
        this.detailBranchFilter.set('');
        this.detailStatusFilter.set(new Set());
        this.activeDetailFilter.set(null);

        this.#loadBranches(item.uuid);
        this.#startPolling(item.uuid);
    }

    closeDetails(): void {
        this.#stopPolling();
        this.selectedImport.set(null);
        this.branches.set([]);
        this.branchesError.set(null);
        this.expandedBranch.set(null);
        this.branchProducts.set([]);
        this.loadData(); // Atualiza grid ao fechar
    }

    get isDetailsModalOpen(): boolean {
        return this.branchesLoading() || this.branchesError() !== null || this.selectedImport() !== null;
    }

    #loadBranches(uuid: string): void {
        this.#service.getBranches(uuid).subscribe({
            next: (data) => {
                this.branches.set(data);
                this.branchesLoading.set(false);

                // Para polling se tudo finalizado
                const hasPendingOrProcessing = data.some(
                    b => b.status === 'PENDING' || b.status === 'PROCESSING'
                );
                if (!hasPendingOrProcessing) {
                    this.#stopPolling();
                }
            },
            error: (err) => {
                console.error('Erro ao carregar filiais:', err);
                this.branchesError.set('Falha ao carregar as filiais.');
                this.branchesLoading.set(false);
            }
        });
    }

    #startPolling(uuid: string): void {
        this.#stopPolling();
        this.isPollingActive.set(true);
        this.#pollingInterval = setInterval(() => {
            this.#service.getBranches(uuid).subscribe({
                next: (data) => {
                    this.branches.set(data);
                    const hasPendingOrProcessing = data.some(
                        b => b.status === 'PENDING' || b.status === 'PROCESSING'
                    );
                    if (!hasPendingOrProcessing) {
                        this.#stopPolling();
                    }
                }
            });
        }, 12000);
    }

    #stopPolling(): void {
        if (this.#pollingInterval) {
            clearInterval(this.#pollingInterval);
            this.#pollingInterval = null;
        }
        this.isPollingActive.set(false);
    }

    // ========== BRANCH ACTIONS ==========

    retryBranch(branchUuid: string): void {
        const importItem = this.selectedImport();
        if (!importItem) return;

        this.retryingBranch.set(branchUuid);

        this.#service.retryBranch(importItem.uuid, branchUuid).subscribe({
            next: () => {
                this.retryingBranch.set(null);
                this.showToast('success', 'Filial reenviada para reprocessamento.');
                this.#loadBranches(importItem.uuid);
                this.#startPolling(importItem.uuid);
            },
            error: (err) => {
                console.error('Erro ao reprocessar filial:', err);
                const customerMsg = err.error?.messageCustomer || 'Falha ao reprocessar a filial.';
                this.showToast('error', customerMsg);
                this.retryingBranch.set(null);
            }
        });
    }

    toggleBranchExpand(branch: DemandImportBranch): void {
        const importItem = this.selectedImport();
        if (!importItem) return;

        if (this.expandedBranch() === branch.uuid) {
            this.expandedBranch.set(null);
            this.branchProducts.set([]);
            return;
        }

        this.expandedBranch.set(branch.uuid);
        this.branchProducts.set([]);
        this.branchProductsLoading.set(true);

        this.#service.getProducts(importItem.uuid, branch.uuid).subscribe({
            next: (products) => {
                this.branchProducts.set(products);
                this.branchProductsLoading.set(false);
            },
            error: (err) => {
                console.error('Erro ao carregar produtos:', err);
                this.branchProducts.set([]);
                this.branchProductsLoading.set(false);
            }
        });
    }

    // ========== GRID FILTER ACTIONS ==========

    toggleFilterPopup(column: GridColumn, event: Event): void {
        event.stopPropagation();
        this.activeFilterColumn.set(this.activeFilterColumn() === column ? null : column);
    }

    closeFilterPopup(): void {
        this.activeFilterColumn.set(null);
    }

    clearFilter(column: GridColumn): void {
        if (column === 'fileName') this.filterFileName.set('');
        if (column === 'demandCode') this.filterDemandCode.set('');
        if (column === 'status') this.filterStatusValues.set(new Set());
        if (column === 'createdAt') {
            this.filterDateStart.set('');
            this.filterDateEnd.set('');
        }
        this.activeFilterColumn.set(null);
    }

    clearAllFilters(): void {
        this.filterFileName.set('');
        this.filterDemandCode.set('');
        this.filterStatusValues.set(new Set());
        this.filterDateStart.set('');
        this.filterDateEnd.set('');
        this.activeFilterColumn.set(null);
    }

    toggleStatusFilter(value: string): void {
        this.filterStatusValues.update(s => {
            const n = new Set(s);
            n.has(value) ? n.delete(value) : n.add(value);
            return n;
        });
    }

    selectAllStatuses(): void {
        this.filterStatusValues.set(new Set(this.statusOptions.map(o => o.value)));
    }

    clearStatusSelection(): void {
        this.filterStatusValues.set(new Set());
    }

    // ========== DETAIL FILTER ACTIONS ==========

    toggleDetailFilterPopup(column: 'branchCode' | 'status', event: Event): void {
        event.stopPropagation();
        this.activeDetailFilter.set(this.activeDetailFilter() === column ? null : column);
    }

    closeDetailFilterPopup(): void {
        this.activeDetailFilter.set(null);
    }

    toggleDetailStatusFilter(value: string): void {
        this.detailStatusFilter.update(s => {
            const n = new Set(s);
            n.has(value) ? n.delete(value) : n.add(value);
            return n;
        });
    }

    clearDetailBranchFilter(): void {
        this.detailBranchFilter.set('');
        this.activeDetailFilter.set(null);
    }

    clearDetailStatusFilter(): void {
        this.detailStatusFilter.set(new Set());
        this.activeDetailFilter.set(null);
    }

    clearAllDetailFilters(): void {
        this.detailBranchFilter.set('');
        this.detailStatusFilter.set(new Set());
        this.activeDetailFilter.set(null);
    }

    // ========== TOAST ==========

    showToast(type: 'success' | 'error', message: string): void {
        this.toast.set({ type, message });
        setTimeout(() => this.toast.set(null), 4000);
    }

    // ========== UI HELPERS ==========

    getStatusLabel(status: string): string {
        switch (status) {
            case 'IMPORTED': return 'Aguardando';
            case 'PENDING': return 'Na Fila';
            case 'PROCESSING': return 'Processando';
            case 'SUCCESS': return 'Concluído';
            case 'ERROR': return 'Erro';
            default: return status;
        }
    }

    getStatusColor(status: string): string {
        switch (status) {
            case 'IMPORTED': return 'bg-blue-50 text-blue-700 border-blue-200';
            case 'PENDING': return 'bg-amber-50 text-amber-700 border-amber-200';
            case 'PROCESSING': return 'bg-orange-50 text-orange-700 border-orange-200';
            case 'SUCCESS': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'ERROR': return 'bg-red-50 text-red-700 border-red-200';
            default: return 'bg-slate-50 text-slate-600 border-slate-200';
        }
    }

    getStatusIcon(status: string) {
        switch (status) {
            case 'IMPORTED': return this.icons.FileText;
            case 'PENDING': return this.icons.Clock;
            case 'PROCESSING': return this.icons.Loader;
            case 'SUCCESS': return this.icons.CheckCircle;
            case 'ERROR': return this.icons.AlertOctagon;
            default: return this.icons.Clock;
        }
    }

    getBranchStatusLabel(status: string): string {
        switch (status) {
            case 'PENDING': return 'Pendente';
            case 'PROCESSING': return 'Processando';
            case 'SUCCESS': return 'Sucesso';
            case 'ERROR': return 'Erro';
            default: return status;
        }
    }

    getBranchStatusColor(status: string): string {
        switch (status) {
            case 'PENDING': return 'bg-slate-50 text-slate-600 border-slate-200';
            case 'PROCESSING': return 'bg-blue-50 text-blue-700 border-blue-200';
            case 'SUCCESS': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'ERROR': return 'bg-red-50 text-red-700 border-red-200';
            default: return 'bg-slate-50 text-slate-600 border-slate-200';
        }
    }

    getBranchStatusIcon(status: string) {
        switch (status) {
            case 'PENDING': return this.icons.Clock;
            case 'PROCESSING': return this.icons.Loader;
            case 'SUCCESS': return this.icons.Check;
            case 'ERROR': return this.icons.AlertOctagon;
            default: return this.icons.Clock;
        }
    }

    getBranchRowColor(status: string): string {
        switch (status) {
            case 'ERROR': return 'border-red-200 bg-red-50/30';
            case 'SUCCESS': return 'border-emerald-200 bg-emerald-50/30';
            case 'PROCESSING': return 'border-blue-200 bg-blue-50/30';
            default: return 'border-slate-200 bg-slate-50/30';
        }
    }

    getBranchIconBg(status: string): string {
        switch (status) {
            case 'ERROR': return 'bg-red-100 text-red-600';
            case 'SUCCESS': return 'bg-emerald-100 text-emerald-600';
            case 'PROCESSING': return 'bg-blue-100 text-blue-600';
            default: return 'bg-slate-100 text-slate-500';
        }
    }

    getProgressBarColor(percentage: number): string {
        if (percentage === 100) return 'bg-emerald-500';
        if (percentage >= 70) return 'bg-blue-500';
        if (percentage >= 40) return 'bg-amber-500';
        return 'bg-red-500';
    }

    sanitizeErrorMessage(message: string | null | undefined): string {
        if (!message) return '';
        let text = message;

        const replacements: [string, string][] = [
            ['ÃƒÂ¡', 'á'], ['ÃƒÂ£', 'ã'], ['ÃƒÂ§', 'ç'], ['ÃƒÂ©', 'é'],
            ['ÃƒÂ­', 'í'], ['ÃƒÂ³', 'ó'], ['ÃƒÂº', 'ú'], ['ÃƒÂ¢', 'â'],
            ['ÃƒÂª', 'ê'], ['ÃƒÂ´', 'ô'], ['ÃƒÂ¼', 'ü'], ['ÃƒÂ', 'à'],
            ['Ã¡', 'á'], ['Ã£', 'ã'], ['Ã§', 'ç'], ['Ã©', 'é'],
            ['Ã­', 'í'], ['Ã³', 'ó'], ['Ãº', 'ú'], ['Ã¢', 'â'],
            ['Ãª', 'ê'], ['Ã´', 'ô'], ['Ã¼', 'ü'], ['Ã ', 'à'],
            ['Ã‰', 'É'], ['Ã‡', 'Ç'], ['Ãƒ', 'Ã'], ['Â', ''],
        ];

        for (const [from, to] of replacements) {
            text = text.split(from).join(to);
        }

        text = text.replace(/\n{2,}/g, '\n').replace(/ {2,}/g, ' ').trim();
        return text;
    }

    formatDate(isoString: string | null): string {
        if (!isoString) return '-';
        // If no timezone indicator is present, JavaScript may parse the string as UTC,
        // causing a timezone shift (e.g. -3h in Brazil). Detect and parse as local time.
        const hasTimezone = isoString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(isoString);
        let d: Date;
        if (!hasTimezone && isoString.includes('T')) {
            const [datePart, timePart] = isoString.split('T');
            const [year, month, day] = datePart.split('-').map(Number);
            const [hours, minutes] = (timePart || '00:00').split(':').map(Number);
            d = new Date(year, month - 1, day, hours, minutes);
        } else {
            d = new Date(isoString);
        }
        if (isNaN(d.getTime())) return '-';
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }

    formatDateOnly(isoString: string | null): string {
        if (!isoString) return '-';
        // Date-only strings (yyyy-MM-dd) are treated as UTC midnight by JavaScript,
        // which causes a day shift in non-UTC timezones (e.g. Brazil UTC-3 shows
        // the previous day). Extract parts directly from the string to avoid this.
        const datePart = isoString.split('T')[0];
        const parts = datePart.split('-');
        if (parts.length !== 3) return '-';
        const [year, month, day] = parts;
        if (!year || !month || !day) return '-';
        return `${day}/${month}/${year}`;
    }

    formatNumber(value: number): string {
        return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
}
